import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FactCheckOutlined } from '@mui/icons-material';
import type { SafetyScanActionPlanPayload } from '../../services/reportsApi';

function text(value?: string | null): string {
  const normalized = String(value || '').trim();
  return normalized || 'Not available';
}

function formatTokenLabel(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function ActionPlanSection(props: {
  actionPlan?: SafetyScanActionPlanPayload | null;
  title?: string;
  subtitle?: string;
}) {
  const actionPlan = props.actionPlan;
  const rows = Array.isArray(actionPlan?.rows) ? actionPlan?.rows || [] : [];
  if (!rows.length) return null;

  const signOff = actionPlan?.sign_off || null;
  const statusLabel = formatTokenLabel(actionPlan?.status);

  return (
    <Box sx={{ mb: 3 }} data-testid="safety-action-plan-section">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            {props.title || 'Action plan'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {props.subtitle || 'Structured corrective controls derived from the signed safety scan executive summary.'}
          </Typography>
        </Box>
        {actionPlan?.status ? (
          <Chip size="small" color="success" variant="outlined" label={statusLabel} />
        ) : null}
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2, boxShadow: 'none' }}>
        <CardContent sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Safety scan action plan">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Risk driver</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Finding</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recommended control</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Control type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Owner</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Due date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Verification</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${text(row.risk_driver)}-${index}`}>
                    <TableCell>{text(row.risk_driver)}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>{text(row.finding)}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>{text(row.recommended_control)}</TableCell>
                    <TableCell>{text(row.control_type)}</TableCell>
                    <TableCell>{text(row.owner)}</TableCell>
                    <TableCell>{text(row.priority)}</TableCell>
                    <TableCell>{text(row.due_date)}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>{text(row.verification_method)}</TableCell>
                    <TableCell>{text(row.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {signOff ? (
        <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: 'none' }} data-testid="safety-action-plan-sign-off">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <FactCheckOutlined color="action" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Sign-off
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  EHS reviewer
                </Typography>
                <Typography variant="body2">{text(signOff.ehs_reviewer)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Site manager
                </Typography>
                <Typography variant="body2">{text(signOff.site_manager)}</Typography>
              </Box>
            </Box>
            {text(actionPlan?.notes) !== 'Not available' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {text(actionPlan?.notes)}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
}
